import * as S from './styles';
import React, { useContext } from 'react';
import { Col, message, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import useTradeStore from '../../hooks/trade/tradeStore';
import BigNumber from 'bignumber.js';
import { useTradeApi } from '../../hooks/trade/tradeApi';
import { useOrderBook } from '../../hooks/trade/orderBook';
import { useTradePosition } from '../../hooks/trade/tradePosition';
import { useTradeSetting } from '../../hooks/trade/tradeSetting';
import { useTradeModalStore } from '../../hooks/trade/modalStore';
import { useIndexPrice } from '../../hooks/trade/indexprice';
import { ThemeContext } from '@/theme/ThemeProvider.tsx';
import { useDisableScroll } from '@utils/useDisableScroll.tsx';
import { formatDecimal } from '@utils/formatDecimal';

import usePercentageStore from '@/hooks/trade/percentageStore';
import { USER_LOG_TYPE, useUserLogger } from '@/app/hooks/userLogger';
import CheckBox from '../CheckBox';
import useMobileDimensions from '../../hooks/useMobileDimensions';
import CBottomSheet from '../Common/CBottomSheet';

import dayjs from 'dayjs';
interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  long_short: string;
}

const CheckPositionModal = ({ open, setOpen, long_short }: IProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMobileDimensions();
  const { createOrder } = useTradeApi(); // ✅ Context 기반 API Hook 사용, Demo/Real 자동 선택
  const { data: tradeStore } = useTradeStore();
  const { textw, raise, fail, borderColor, textGrey } =
    useContext(ThemeContext).theme.COLORS;

  const { getPrice } = useIndexPrice(tradeStore?.ep_id);
  const { data: tradeModalStore, mutate: mutateTradeModalStore } =
    useTradeModalStore();
  const { data: tradeSetting } = useTradeSetting(tradeStore?.ep_id);
  const { mutate: mutateOrderBook } = useOrderBook(tradeStore?.ep_id);
  const { mutateUserLog, getOrderLog } = useUserLogger();
  const { mutate: mutatePosition } = useTradePosition();
  const main = tradeStore?.pairs?.split('/')[0];
  const currency = tradeStore?.pairs?.split('/')[1];

  //const tradeData = useBinanceWebSocketStore((s) => s.tradeData);
  const globalPercentage = usePercentageStore((state) => state.percentage);

  const sendOrder = async () => {
    if (!tradeStore?.order_amount)
      return message.error(t('pleaseFillAllFields') + '');

    if (tradeStore?.tpsl) {
      if (isNaN(tradeStore?.tp) || isNaN(tradeStore?.sl)) {
        return message.error('CHECK TP/SL');
      }
    }

    // 현재 시장 가격 가져오기
    const currentMarketPrice = getPrice(tradeStore?.ep_id);

    // 지정가 주문 여부 확인
    const isLimitOrder =
      tradeStore?.limit_market?.toUpperCase() == 'L' ||
      tradeStore?.limit_market?.toUpperCase() == 'LIMIT';

    // 최종 주문 타입 결정
    let finalOrderType = isLimitOrder ? 'L' : 'M';

    if (isLimitOrder) {
      // 주문 가격과 현재 가격 비교
      const orderPrice = Number(tradeStore?.order_price);

      // 불리한 가격인지 확인
      const isPriceUnfavorable =
        (long_short === 'L' && orderPrice > currentMarketPrice) || // 매수인데 지정가가 시장가보다 높음
        (long_short === 'S' && orderPrice < currentMarketPrice); // 매도인데 지정가가 시장가보다 낮음

      // 불리한 가격이면 시장가로 전환
      if (isPriceUnfavorable) {
        finalOrderType = 'M';
        // message.warning('불리한 가격으로 시장가 전환됨');
      }
    }



    const orderParams: any = {
      ep_id: tradeStore?.ep_id,
      main_sub: localStorage.getItem('main_sub') || 'M',
      long_short: long_short,
      cross_iso: tradeSetting?.cross_iso,
      leverage: tradeSetting?.leverage,
      reduce_position: tradeStore.reduce_only ? 1 : 0,
      post_only: tradeStore?.post_only ? 1 : 0,
      limit_market: finalOrderType,
      price: tradeStore?.order_price,
      amount: new BigNumber(tradeStore?.order_amount)
        .dividedBy(tradeSetting?.leverage)
        .toNumber(),
      tpsl: tradeStore?.tpsl,
      tp: tradeStore?.tp,
      sl: tradeStore?.sl,
      tptype: tradeStore?.tptype,
      sltype: tradeStore?.sltype,
      full_gauge: globalPercentage === 100 ? true : false,
    };

    // Time In Force 파라미터 추가 (지정가 주문일 때만)
    if (finalOrderType === 'L') {
      const timeInForce = tradeStore?.time_in_force || 'GTC';
      orderParams.time_in_force = timeInForce;

      if (timeInForce === 'GTD' && tradeStore?.gtd_expiry) {
        orderParams.gtd_expiry = tradeStore.gtd_expiry;
      }
    }

    const result = await createOrder(orderParams); // ✅ Context 기반 API Hook 사용, Demo/Real 자동 선택
    //mutateStore({ ...tradeStore, order_amount: 0 })

    if (result.result.success) {
      mutateUserLog({
        log: getOrderLog(orderParams, USER_LOG_TYPE.ORDER_OPEN),
        data: orderParams,
      });
      mutateOrderBook();
      mutatePosition();

      setTimeout(() => {
        mutateOrderBook();
        mutatePosition(null);
      }, 500);

      message.success(t('ordersuccess'));
    } else {
      if (result?.result?.message == 'ERROR_MAXAMOUNT') {
        message.error(
          t('ERROR_MAXAMOUNT') +
            ' / MAX AMOUNT : ' +
            BigNumber(result?.result?.max).toFormat(3)
        );
      } else {
        message.error(t('unknownError'));
      }
    }
  };

  let margin = new BigNumber(tradeStore?.order_amount)
    .multipliedBy(
      tradeStore?.limit_market == 'M'
        ? getPrice(tradeStore?.ep_id)
        : tradeStore?.order_price
    )
    .dividedBy(tradeSetting?.leverage)
    .toFormat(4);

  if (tradeStore?.main_sub == 'S') {
    margin = new BigNumber(tradeStore?.order_amount)
      .dividedBy(tradeSetting?.leverage)
      .toFormat(3);
  }

  useDisableScroll(open);

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const longShortColor = long_short === 'L' ? raise : fail;
  const orderTypeText =
    tradeStore?.limit_market === 'M' ? t('market') : t('limit');
  const orderActionText =
    long_short === 'L'
      ? `${orderTypeText} ${t('buy')}`
      : `${orderTypeText} ${t('sell')}`;

  // 콘텐츠 렌더링 함수
  const renderContent = () => (
    <div onTouchMove={handleTouchMove}>
      {/* 모바일 BottomSheet용 헤더 */}
      {isMobile && (
        <S.MobileHeader>
          <S.MobileTitle>{t('ordercheck')}</S.MobileTitle>
        </S.MobileHeader>
      )}

      {/* 코인 정보 요약 영역 */}
      <S.SummaryBox>
        <S.SummaryText>{tradeStore?.pairs?.replace('/', '')}</S.SummaryText>
        <S.Dot />
        <S.SummaryText>{tradeSetting?.leverage}x</S.SummaryText>
        <S.Dot />
        <S.SummaryText style={{ color: longShortColor }}>
          {orderActionText}
        </S.SummaryText>
      </S.SummaryBox>

      {/* 상세 정보 목록 */}
      <S.InfoList>
        {/* 주문 유형 */}
        <S.InfoRow>
          <S.Label>{t('orderType')}</S.Label>
          <S.Value>{orderTypeText}</S.Value>
        </S.InfoRow>

        {/* 주문 가격 */}
        <S.InfoRow>
          <S.Label>{t('price')}</S.Label>
          <S.Value>
            {tradeStore?.limit_market === 'M'
              ? `${formatDecimal(getPrice(tradeStore?.ep_id), tradeSetting?.dp || 2)} ${currency}`
              : `${formatDecimal(tradeStore?.order_price, tradeSetting?.dp || 2)} ${currency}`}
          </S.Value>
        </S.InfoRow>

        {/* 수량 */}
        <S.InfoRow>
          <S.Label>{t('amount')}</S.Label>
          <S.Value>
            {formatDecimal(tradeStore?.order_amount, 8)}{' '}
            {tradeStore?.main_sub === 'S' ? currency : main}
          </S.Value>
        </S.InfoRow>

        {/* 마진 모드 */}
        <S.InfoRow>
          <S.Label>{t('marginMode')}</S.Label>
          <S.Value>
            {t(tradeSetting?.cross_iso === 'C' ? 'cross' : 'iso')}
          </S.Value>
        </S.InfoRow>

        {/* 증거금 */}
        <S.InfoRow>
          <S.Label>{t('margin')}</S.Label>
          <S.Value>
            {margin} {currency}
          </S.Value>
        </S.InfoRow>

        {/* 포지션 감소 */}
        {tradeStore?.reduce_only !== undefined && (
          <S.InfoRow>
            <S.Label>{t('reduceOnly')}</S.Label>
            <S.Value
              style={{ color: tradeStore?.reduce_only ? raise : '#ffffff' }}
            >
              {tradeStore?.reduce_only ? t('yes') : t('no')}
            </S.Value>
          </S.InfoRow>
        )}
      </S.InfoList>

      {/* Time In Force (지정가 주문일 때만) */}
      {tradeStore?.limit_market === 'L' && (
          <Row style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: '10px 0',
            alignItems: 'center'
          }}>
          <Col span={8}>
            <span style={{ color: textGrey, fontSize: '13px' }}>
              {t('timeInForce') || 'Time In Force'}
            </span>
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: textw }}>
              {tradeStore?.time_in_force === 'GTD'
                ? `GTD (${dayjs(tradeStore?.gtd_expiry).format('MM/DD HH:mm')})`
                  : 'GTC'
                }
            </span>
          </Col>
        </Row>
      )}

      {/* 다시 보지 않기 체크박스 */}
      <S.CheckboxWrapper>
        <CheckBox
          checked={tradeModalStore?.skip}
          setChecked={(checked) => {
            mutateTradeModalStore({
              ...tradeModalStore,
              skip: checked,
            });
          }}
        />
        <S.CheckboxLabel style={{ color: textw }}>
          {t('notagain')}
        </S.CheckboxLabel>
      </S.CheckboxWrapper>

      {/* 버튼 영역 */}
      <S.ButtonContainer>
        <S.CancelButton onClick={() => setOpen(false)}>
          {t('cancel')}
        </S.CancelButton>
        <S.ConfirmButton
          $bgColor={longShortColor}
          onClick={() => {
            sendOrder();
            setOpen(false);
          }}
        >
          {t('confirm')}
        </S.ConfirmButton>
      </S.ButtonContainer>
    </div>
  );

  // 모바일: CBottomSheet, 데스크톱: Modal
  if (isMobile) {
    return (
      <CBottomSheet isOpen={open} onClose={() => setOpen(false)} height="auto">
        <S.BottomSheetContent>{renderContent()}</S.BottomSheetContent>
      </CBottomSheet>
    );
  }

  return (
    <S.CustomModal
      className={'custom-modal check-position-modal'}
      open={open}
      title={t('ordercheck') + ''}
      onCancel={() => setOpen(false)}
      footer={null}
      centered
      styles={{
        header: {
          marginBottom: '16px',
        },
        body: {
          padding: '0',
        },
      }}
    >
      {renderContent()}
    </S.CustomModal>
  );
};

export default React.memo(CheckPositionModal);
